import { useRouter } from '@tanstack/react-router';
import { usePermissions } from '@customerio/hydra-permissions/react';
import {
  Banner,
  Button,
  Emphasis,
  Icon,
  EmptyState,
  EmptyStateActions,
  EmptyStateDescription,
  EmptyStateFooter,
  EmptyStateTitle,
  Image,
  Badge,
  Link,
  List,
  ListItem,
  Page,
  PageBody,
  PageHeader,
  PageHeaderActions,
  PageHeaderTitle,
  InlineStack,
  Stack,
  Text,
  Tooltip,
} from '@customerio/pluma-components/react';
import { isEmptyString } from '@/utils/string';
import {
  getWarehouseDestinationImageSrc,
  getWarehouseDestinationName,
  getWarehouseDocumentationUrl,
} from '@/features/warehouse-sync';
import {
  canUpdateWarehouseSchema,
  WAREHOUSE_DESTINATION_CONFIG_KEYS,
  warehouseSyncType,
  type WarehouseDestination,
  type WarehouseSync,
} from '@/services/db/warehouse-sync/entity';
import { Details } from './components/details';
import { UpdateSchemaVersion } from './components/update-schema-version';
import { BucketConfiguration } from './components/bucket-configuration';
import { TipsAndResources } from './components/tips-and-resources';
import { useOptionTerms } from './hooks';
import { useConfigActions } from './use-config-actions';
import * as styles from './components/details.css';

type WarehouseConfigPageProps = {
  workspaceId: string;
  destination: WarehouseDestination;
  sync: WarehouseSync | null;
  showPremiumLabel: boolean;
  isTwilioEnabled: boolean;
};

/** The bucket a sync writes to. Azure has no bucket name, so its path stands in. */
function bucketLabel(sync: WarehouseSync, destination: WarehouseDestination): string {
  const config = sync[WAREHOUSE_DESTINATION_CONFIG_KEYS[destination]];
  if (config == null) return '';
  return 'bucket_name' in config ? config.bucket_name : config.bucket_path;
}

/**
 * Routes to the connected view or the blank slate. Split so the connected view
 * can own the write actions — a workspace with no sync here has nothing to act on.
 */
export default function ConfigPage({
  workspaceId,
  destination,
  sync,
  showPremiumLabel,
  isTwilioEnabled,
}: WarehouseConfigPageProps) {
  const syncedDestination = warehouseSyncType(sync);

  if (sync == null || syncedDestination !== destination) {
    return (
      <WarehouseBlankSlate
        workspaceId={workspaceId}
        destination={destination}
        otherDestination={sync != null && syncedDestination !== '' ? syncedDestination : null}
        showPremiumLabel={showPremiumLabel}
      />
    );
  }

  return (
    <ConnectedSyncView
      workspaceId={workspaceId}
      destination={destination}
      sync={sync}
      showPremiumLabel={showPremiumLabel}
      isTwilioEnabled={isTwilioEnabled}
    />
  );
}

type ConnectedSyncViewProps = Omit<WarehouseConfigPageProps, 'sync'> & { sync: WarehouseSync };

function ConnectedSyncView({
  workspaceId,
  destination,
  sync,
  showPremiumLabel,
  isTwilioEnabled,
}: ConnectedSyncViewProps) {
  const terms = useOptionTerms();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission('journeys/exports/edit');
  const canDelete = hasPermission('journeys/exports/delete');
  const destinationName = getWarehouseDestinationName(destination);

  const actions = useConfigActions({
    workspaceId,
    sync,
    destination,
    destinationName,
    disconnectMessage: (
      <Stack gap="150">
        <List styleType="none" gap="100">
          <ListItem>
            <InlineStack gap="100" alignBlock="start">
              <Icon name="error" color="critical" size="sm" />
              <Text>Files will no longer export to {destinationName}.</Text>
            </InlineStack>
          </ListItem>
          <ListItem>
            <InlineStack gap="100" alignBlock="start">
              <Icon name="error" color="critical" size="sm" />
              <Text>Your bucket configuration and history of synced files will be deleted from Customer.io.</Text>
            </InlineStack>
          </ListItem>
        </List>
        <Text>Are you sure you want to disconnect sync to {destinationName}?</Text>
      </Stack>
    ),
  });

  return (
    <Page>
      <PageHeader>
        <PageHeaderTitle data-test-page-title>
          {destinationName}
          {showPremiumLabel ? (
            <Badge data-test-premium-label ml="100" color="grey">
              Premium
            </Badge>
          ) : null}
        </PageHeaderTitle>
        <PageHeaderActions>
          <Button
            data-test-warehouse-delete-sync
            variant="secondary"
            isDanger
            isDisabled={!canDelete}
            isLoading={actions.isDeleting}
            onClick={actions.disconnect}
          >
            Disconnect sync
          </Button>
        </PageHeaderActions>
      </PageHeader>

      <PageBody>
        <div className={styles.configLayout}>
          {!isEmptyString(sync.error) ? (
            <Banner
              data-test-warehouse-sync-detail-error-banner
              variant="error"
              title="We could not sync to this database"
              className={styles.alertArea}
            >
              Check the {destinationName} bucket <Emphasis>{bucketLabel(sync, destination)}</Emphasis>&apos;s settings
              to ensure you have permitted us to write to it
            </Banner>
          ) : null}

          <div className={styles.detailsArea}>
            <Details
              sync={sync}
              destination={destination}
              isReadOnly={!canEdit || actions.isTogglingOption}
              isTwilioEnabled={isTwilioEnabled}
              terms={terms}
              upgradeAlerts={actions.upgradeAlerts}
              onToggleOption={actions.toggleOption}
              onUpgradeSchema={() => actions.setIsSchemaModalOpen(true)}
            />
          </div>

          <Stack gap="200" className={styles.metaArea}>
            <BucketConfiguration
              sync={sync}
              destination={destination}
              isReadOnly={!canEdit}
              isUpdating={actions.isSavingBucketConfig}
              onManageOpened={actions.startBucketConfigUpdate}
              onUpdate={actions.updateBucketConfig}
            />

            {canUpdateWarehouseSchema(sync) ? (
              <UpdateSchemaVersion
                version={sync.version}
                isOpen={actions.isSchemaModalOpen}
                onOpenChange={actions.setIsSchemaModalOpen}
                isReadOnly={!canEdit}
                isTwilioEnabled={isTwilioEnabled}
                isUpgrading={actions.isUpgradingSchema}
                terms={terms}
                onUpgrade={actions.upgradeSchema}
              />
            ) : (
              <TipsAndResources workspaceId={workspaceId} destinationName={destinationName} />
            )}
          </Stack>
        </div>
      </PageBody>
    </Page>
  );
}

type WarehouseBlankSlateProps = {
  workspaceId: string;
  destination: WarehouseDestination;
  /** The destination the workspace already syncs to, when it is a different one. */
  otherDestination: WarehouseDestination | null;
  showPremiumLabel: boolean;
};

/**
 * Shown when this destination has no sync — either the workspace has none, or it
 * syncs somewhere else. A workspace can only sync to one storage service, so the
 * second case disables the connect button and points at the existing sync.
 */
function WarehouseBlankSlate({
  workspaceId,
  destination,
  otherDestination,
  showPremiumLabel,
}: WarehouseBlankSlateProps) {
  const router = useRouter();
  const destinationName = getWarehouseDestinationName(destination);
  const connectHref = router.buildLocation({
    to: '/workspaces/$workspaceId/integrations/warehouse/$type/sync',
    params: { workspaceId, type: destination },
  }).href;

  const connectButton = (
    <Button
      data-test-warehouse-button="start-sync"
      variant="primary"
      isDisabled={otherDestination != null}
      href={otherDestination == null ? connectHref : undefined}
    >
      Sync your {destinationName} bucket
    </Button>
  );

  return (
    <EmptyState data-test-warehouse-blank-slate maxW="full">
      <EmptyStateTitle>
        <Stack gap="300" alignInline="center">
          <Image src={getWarehouseDestinationImageSrc(destination)} alt="" size="icon-v2-xl" />
          {showPremiumLabel ? (
            <Badge data-test-premium-label color="grey">
              Premium
            </Badge>
          ) : null}
          <span>Sync your data to {destinationName}</span>
        </Stack>
      </EmptyStateTitle>

      <EmptyStateDescription maxW="full">
        Automatically export your Customer.io data every 15 minutes to a {destinationName} bucket. From there, you can
        ingest your data in any data warehouse.
      </EmptyStateDescription>

      <EmptyStateActions>
        <Stack gap="150" alignInline="center">
          {otherDestination != null ? (
            <Tooltip
              as="span"
              content={`Your data can be synced to only one storage service. Disable your sync to ${getWarehouseDestinationName(otherDestination)} before creating a sync to ${destinationName}.`}
            >
              {connectButton}
            </Tooltip>
          ) : (
            connectButton
          )}

          {otherDestination != null ? (
            <Link
              icon="chevron-right"
              iconPosition="trailing"
              variant="primary"
              isUnderlined={false}
              href={
                router.buildLocation({
                  to: '/workspaces/$workspaceId/integrations/warehouse/$type',
                  params: { workspaceId, type: otherDestination },
                }).href
              }
            >
              Go to your {getWarehouseDestinationName(otherDestination)} sync
            </Link>
          ) : null}
        </Stack>
      </EmptyStateActions>

      <EmptyStateFooter>
        Learn more about{' '}
        <Link
          isExternal={true}
          variant="secondary"
          href={getWarehouseDocumentationUrl(destination)}
          data-test-warehouse-documentation-url
        >
          syncing to {destinationName}
        </Link>
      </EmptyStateFooter>
    </EmptyState>
  );
}
